import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { SortButton } from "@/components/admin/sort-button";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDistance } from "date-fns";
import { ChevronLeft, UserPlus } from "lucide-react";
import {
  RecordContextProvider,
  ShowBase,
  useGetIdentity,
  useListContext,
  useRecordContext,
  useShowContext,
} from "ra-core";
import {
  Link,
  Link as RouterLink,
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useIsMobile } from "@/hooks/use-mobile";
import { ActivityLog } from "../activity/ActivityLog";
import { Avatar } from "../contacts/Avatar";
import { TagsList } from "../contacts/TagsList";
import { findDealLabel } from "../deals/deal";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Contact, Deal } from "../types";
import { canUseCrmPermission } from "../providers/commons/crmPermissions";
import { useCanViewAmounts } from "@/lib/permissions/useMaskedAmount";
import {
  ScrollableContentArea,
  StickyTabsBar,
} from "@/components/atomic-crm/layout/page-shell";
import {
  AdditionalInfo,
  AddressInfo,
  CompanyAside,
  CompanyInfo,
  ContextInfo,
} from "./CompanyAside";
import { CompanyAvatar } from "./CompanyAvatar";

export const CompanyShow = () => {
  const isMobile = useIsMobile();

  return (
    <ShowBase>
      {isMobile ? <CompanyShowContentMobile /> : <CompanyShowContent />}
    </ShowBase>
  );
};

const CompanyShowContentMobile = () => {
  const { record, isPending } = useShowContext<Company>();
  const location = useLocation();
  const navigate = useNavigate();
  if (isPending || !record) return null;

  return (
    <>
      <MobileHeader>
        <MobileBackButton to="/" />
        <div className="flex flex-1">
          <Link to="/">
            <h1 className="text-lg font-semibold">Company</h1>
          </Link>
        </div>
      </MobileHeader>

      <MobileContent>
        <Button
          type="button"
          variant="ghost"
          className="mb-4 px-0 link-action"
          onClick={() => navigate(location.state?.from ?? -1)}
        >
          <ChevronLeft className="size-4" />
          Back
        </Button>
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <CompanyAvatar />
            <div className="mx-3 flex-1">
              <h2 className="text-lg font-semibold">{record.name}</h2>
            </div>
          </div>
        </div>
        <CompanyInfo record={record} />
        <AddressInfo record={record} />
        <ContextInfo record={record} />
        <AdditionalInfo record={record} />
      </MobileContent>
    </>
  );
};

type CompanyTab = "activity" | "contacts" | "deals";

const getValidCompanyTab = (value?: string | null): CompanyTab =>
  value === "contacts" || value === "deals" ? value : "activity";

export const CompanyShowContent = () => {
  const { record, isPending } = useShowContext<Company>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: identity } = useGetIdentity();
  const canManageSales = canUseCrmPermission(identity as any, "sales.manage");

  // Get tab from URL or default to "activity"
  const tabMatch = useMatch("/companies/:id/show/:tab");
  const isShowRoute = !!useMatch("/companies/:id/show") || !!tabMatch;
  const currentTab = getValidCompanyTab(
    (tabMatch?.params?.tab as string | undefined) ?? searchParams.get("tab"),
  );

  const handleTabChange = (value: string) => {
    const nextTab = getValidCompanyTab(value);
    if (nextTab === currentTab) return;

    if (isShowRoute) {
      if (nextTab === "activity") {
        navigate(`/companies/${record?.id}/show`);
        return;
      }
      navigate(`/companies/${record?.id}/show/${nextTab}`);
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextTab === "activity") {
      nextSearchParams.delete("tab");
    } else {
      nextSearchParams.set("tab", nextTab);
    }
    setSearchParams(nextSearchParams, { replace: true });
  };

  if (isPending || !record) return null;

  return (
    <div className="mt-2 flex pb-2 gap-8">
      <div className="flex-1">
        <Card>
          <CardContent>
            <Button
              type="button"
              variant="ghost"
              className="mb-3 px-0 link-action"
              onClick={() => navigate(location.state?.from ?? -1)}
            >
              <ChevronLeft className="size-4" />
              Back
            </Button>
            <div className="flex mb-3">
              <CompanyAvatar />
              <h5 className="text-lg font-semibold ml-2 flex-1">
                {record.name}
              </h5>
            </div>
            <Tabs
              defaultValue={currentTab}
              onValueChange={handleTabChange}
              className="flex min-h-0 flex-col"
            >
              <StickyTabsBar className="pb-2">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                  <TabsTrigger value="contacts">
                    {record.nb_contacts
                      ? record.nb_contacts === 1
                        ? "1 Contact"
                        : `${record.nb_contacts} Contacts`
                      : "No Contacts"}
                  </TabsTrigger>
                  {record.nb_deals ? (
                    <TabsTrigger value="deals">
                      {record.nb_deals === 1
                        ? "1 project"
                        : `${record.nb_deals} projects`}
                    </TabsTrigger>
                  ) : null}
                </TabsList>
              </StickyTabsBar>
              <ScrollableContentArea>
                <TabsContent value="activity" className="pt-2">
                  <ActivityLog companyId={record.id} context="company" />
                </TabsContent>
                <TabsContent value="contacts">
                  {record.nb_contacts ? (
                    <ReferenceManyField
                      reference="contacts_summary"
                      target="company_id"
                      sort={{ field: "last_name", order: "ASC" }}
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-row justify-end space-x-2 mt-1">
                          {!!record.nb_contacts && (
                            <SortButton
                              fields={["last_name", "first_name", "last_seen"]}
                            />
                          )}
                          {canManageSales ? (
                            <CreateRelatedContactButton />
                          ) : null}
                        </div>
                        <ContactsIterator />
                      </div>
                    </ReferenceManyField>
                  ) : (
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-row justify-end space-x-2 mt-1">
                        {canManageSales ? <CreateRelatedContactButton /> : null}
                      </div>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="deals">
                  {record.nb_deals ? (
                    <ReferenceManyField
                      reference="deals"
                      target="company_id"
                      sort={{ field: "name", order: "ASC" }}
                    >
                      <ProjectsIterator />
                    </ReferenceManyField>
                  ) : null}
                </TabsContent>
              </ScrollableContentArea>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <CompanyAside />
    </div>
  );
};

const ContactsIterator = () => {
  const location = useLocation();
  const { data: contacts, error, isPending } = useListContext<Contact>();

  if (isPending || error) return null;

  const now = Date.now();
  return (
    <div className="pt-0">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <div className="p-0 text-sm">
            <RouterLink
              to={`/contacts/${contact.id}/show`}
              state={{ from: location.pathname }}
              className="link-action flex items-center justify-between hover:bg-accent py-2 transition-colors"
            >
              <div className="mr-4">
                <Avatar />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">
                  {`${contact.first_name} ${contact.last_name}`}
                </div>
                <div className="text-sm text-muted-foreground">
                  {contact.title}
                  {contact.nb_tasks
                    ? ` - ${contact.nb_tasks} task${
                        contact.nb_tasks > 1 ? "s" : ""
                      }`
                    : ""}
                  &nbsp; &nbsp;
                  <TagsList />
                </div>
              </div>
              {contact.last_seen && (
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">
                    last activity {formatDistance(contact.last_seen, now)} ago{" "}
                    <Status status={contact.status} />
                  </div>
                </div>
              )}
            </RouterLink>
          </div>
        </RecordContextProvider>
      ))}
    </div>
  );
};

const CreateRelatedContactButton = () => {
  const company = useRecordContext<Company>();
  return (
    <Button variant="secondary" asChild size="sm" className="h-9">
      <RouterLink
        to="/contacts/create"
        state={company ? { record: { company_id: company.id } } : undefined}
        className="flex items-center gap-2"
      >
        <UserPlus className="h-4 w-4" />
        Add contact
      </RouterLink>
    </Button>
  );
};

const DealCompanyListMeta = ({
  deal,
  dealStages,
  dealCategories,
}: {
  deal: Deal;
  dealStages: ReturnType<typeof useConfigurationContext>["dealStages"];
  dealCategories: ReturnType<typeof useConfigurationContext>["dealCategories"];
}) => {
  const canViewAmounts = useCanViewAmounts();
  const amountPart = canViewAmounts
    ? deal.amount.toLocaleString("en-US", {
        notation: "compact",
        style: "currency",
        currency: "USD",
        currencyDisplay: "narrowSymbol",
        minimumSignificantDigits: 3,
      })
    : "—";

  return (
    <>
      {findDealLabel(dealStages, deal.stage)}, {amountPart}
      {deal.category
        ? `, ${dealCategories.find((c) => c.value === deal.category)?.label ?? deal.category}`
        : ""}
    </>
  );
};

const ProjectsIterator = () => {
  const { data: deals, error, isPending } = useListContext<Deal>();
  const { dealStages, dealCategories } = useConfigurationContext();
  if (isPending || error) return null;

  const now = Date.now();
  return (
    <div>
      <div>
        {deals.map((deal) => (
          <div key={deal.id} className="p-0 text-sm">
            <RouterLink
              to={`/deals/${deal.id}/show`}
              className="flex items-center justify-between hover:bg-accent py-2 px-4 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium">{deal.name}</div>
                <div className="text-sm text-muted-foreground">
                  <DealCompanyListMeta
                    deal={deal}
                    dealStages={dealStages}
                    dealCategories={dealCategories}
                  />
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">
                  last activity {formatDistance(deal.updated_at, now)} ago{" "}
                </div>
              </div>
            </RouterLink>
          </div>
        ))}
      </div>
    </div>
  );
};
